import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert } from 'react-bootstrap';
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaPaperPlane } from 'react-icons/fa';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
    honeypot: '' // Hidden field for bot detection
  });
  
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [lastSubmissionTime, setLastSubmissionTime] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  
  // Rate limiting: prevent submissions within 60 seconds
  const SUBMISSION_COOLDOWN = 60000; // 60 seconds
  const MAX_SUBMISSIONS_PER_HOUR = 5;
  const submissionHistory = useRef([]);
  
  // Check if user can submit (rate limiting)
  const canSubmit = () => {
    const now = Date.now();
    
    // Check cooldown period
    if (lastSubmissionTime && (now - lastSubmissionTime) < SUBMISSION_COOLDOWN) {
      return false;
    }
    
    // Check hourly limit
    const oneHourAgo = now - 3600000; // 1 hour in milliseconds
    const recentSubmissions = submissionHistory.current.filter(time => time > oneHourAgo);
    
    if (recentSubmissions.length >= MAX_SUBMISSIONS_PER_HOUR) {
      return false;
    }
    
    return true;
  };
  
  // Get remaining cooldown time
  const getCooldownTime = () => {
    if (!lastSubmissionTime) return 0;
    const elapsed = Date.now() - lastSubmissionTime;
    const remaining = SUBMISSION_COOLDOWN - elapsed;
    return Math.max(0, Math.ceil(remaining / 1000));
  };

  // Update cooldown timer every second
  useEffect(() => {
    const timer = setInterval(() => {
      setCooldownTime(getCooldownTime());
    }, 1000);

    return () => clearInterval(timer);
  }, [lastSubmissionTime]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }
    
    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    // Phone validation (optional but if provided, must be valid)
    if (formData.phone && formData.phone.trim().length < 10) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    
    // Message validation with quality checks
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 20) {
      newErrors.message = 'Message must be at least 20 characters to be meaningful';
    } else if (formData.message.trim().length < 50) {
      newErrors.message = 'Please provide more details about your requirements (minimum 50 characters)';
    } else if (formData.message.toLowerCase().includes('test') || 
               formData.message.toLowerCase().includes('demo') ||
               formData.message.toLowerCase().includes('sample')) {
      newErrors.message = 'Please provide a real inquiry, not a test message';
    }
    
    // Service validation
    if (!formData.service) {
      newErrors.service = 'Please select a service of interest';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check honeypot field (bot detection)
    if (formData.honeypot) {
      console.log('Bot detected - honeypot field filled');
      return; // Silently fail for bots
    }
    
    // Rate limiting check
    if (!canSubmit()) {
      const cooldownTime = getCooldownTime();
      if (cooldownTime > 0) {
        alert(`Please wait ${cooldownTime} seconds before submitting again. This prevents spam and ensures we can give each inquiry proper attention.`);
      } else {
        alert('You have reached the maximum number of submissions per hour. Please try again later or contact us directly by phone.');
      }
      return;
    }
    
    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // EmailJS configuration
      const SERVICE_ID = 'service_on82dfn';
      const TEMPLATE_ID = 'template_aqp8s69';
      const PUBLIC_KEY = 'WKlCThVM_vmCoj2IH';
      
      // Prepare email template parameters
      const templateParams = {
        from_name: formData.name,
        from_email: formData.email,
        company: formData.company,
        phone: formData.phone,
        service: formData.service,
        message: formData.message,
        to_email: 'info@nikdip.com' // Where to send the email
      };
      
      // Send email using EmailJS
      const response = await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        templateParams,
        PUBLIC_KEY
      );
      
      console.log('Email sent successfully:', response);
      
      // Update submission tracking
      const now = Date.now();
      setLastSubmissionTime(now);
      submissionHistory.current.push(now);
      
      setSubmitted(true);
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setIsSubmitting(false);
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          service: '',
          message: '',
          honeypot: ''
        });
        setErrors({});
      }, 3000);
      
    } catch (error) {
      console.error('Error sending email:', error);
      setIsSubmitting(false);
      
      // Show error message to user
      alert('Failed to send message. Please try again or contact us directly at victor.o@nikdipmultinational.com');
    }
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row className="align-items-center">
            <Col lg={8}>
              <h1 className="display-4 fw-bold mb-4">Contact Us</h1>
              <p className="lead mb-4">
                Get in touch with our water treatment experts for technical consultation, 
                service requests, or to discuss your specific requirements.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Information & Form */}
      <section className="section-padding">
        <Container>
          <Row>
            {/* Contact Information */}
            <Col lg={4} className="mb-4">
              <h2 className="mb-4">Get in Touch</h2>
              
              <Card className="mb-3 border-0 shadow-sm">
                <Card.Body className="p-4">
                  <div className="d-flex align-items-start mb-4">
                    <div className="feature-icon me-3">
                      <FaEnvelope />
                    </div>
                    <div>
                      <h5>Email</h5>
                      <p className="mb-0">victor.o@nikdipmultinational.com</p>
                    </div>
                  </div>
                  
                  <div className="d-flex align-items-start mb-4">
                    <div className="feature-icon me-3">
                      <FaPhone />
                    </div>
                    <div>
                      <h5>Phone</h5>
                      <p className="mb-0">+234-9061743394
                      <br />
                      +234-8131210262
                      <br />
                      +234-8066638387 </p>
                      <small className="text-muted">Mon-Fri: 8AM-6PM</small>
                    </div>
                  </div>
                  
                  <div className="d-flex align-items-start mb-4">
                    <div className="feature-icon me-3">
                      <FaMapMarkerAlt />
                    </div>
                    <div>
                      <h5>Office Location</h5>
                      <p className="mb-0">No. 06, WALE ALOMO CLOSE,
                      <br />
                      OFF BALOGUN ROAD,
                      <br />
                      IJU-ISHAGA, LAGOS STATE. NIGERIA.</p>
                    </div>
                  </div>
                  
                  <div className="d-flex align-items-start">
                    <div className="feature-icon me-3">
                      <FaClock />
                    </div>
                    <div>
                      <h5>Business Hours</h5>
                      <p className="mb-0">Monday - Friday: 8:00 AM - 6:00 PM</p>
                      <small className="text-muted">Emergency support available 24/7</small>
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            {/* Contact Form */}
            <Col lg={8} className="mb-4">
              <Card className="border-0 shadow-sm">
                <Card.Body className="p-4">
                  <h2 className="mb-4">Send us a Message</h2>
                  
                  {submitted && (
                    <Alert variant="success" className="mb-4">
                      <strong>Thank you for your message!</strong> We'll get back to you within 24 hours.
                    </Alert>
                  )}

                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group controlId="name">
                          <Form.Label>Full Name *</Form.Label>
                          <Form.Control
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            isInvalid={!!errors.name}
                            placeholder="John Doe"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.name}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                      
                      <Col md={6} className="mb-3">
                        <Form.Group controlId="email">
                          <Form.Label>Email Address *</Form.Label>
                          <Form.Control
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            isInvalid={!!errors.email}
                            placeholder="john@example.com"
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors.email}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row>
                      <Col md={6} className="mb-3">
                        <Form.Group controlId="company">
                          <Form.Label>Company Name</Form.Label>
                          <Form.Control
                            type="text"
                            name="company"
                            value={formData.company}
                            onChange={handleInputChange}
                            placeholder="Your Company Ltd."
                          />
                        </Form.Group>
                      </Col>
                      
                      <Col md={6} className="mb-3">
                        <Form.Group controlId="phone">
                          <Form.Label>Phone Number</Form.Label>
                          <Form.Control
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleInputChange}
                            placeholder="+234 XXX XXX XXXX"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Form.Group className="mb-3">
                      <Form.Label htmlFor="service">Service Interest</Form.Label>
                      <Form.Select
                        id="service"
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                      >
                        <option value="">Select a service...</option>
                        <option value="chemical-supply">Chemical Supply</option>
                        <option value="technical-services">Technical Services</option>
                        <option value="engineering-projects">Engineering & Projects</option>
                        <option value="equipment">Equipment & Accessories</option>
                        <option value="consultation">General Consultation</option>
                        <option value="emergency">Emergency Support</option>
                      </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label htmlFor="message">Message *</Form.Label>
                      <Form.Control
                        as="textarea"
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        isInvalid={!!errors.message}
                        rows={5}
                        placeholder="Please describe your requirements or questions in detail (minimum 50 characters)..."
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.message}
                      </Form.Control.Feedback>
                    </Form.Group>

                    {/* Honeypot field for bot detection - hidden from users */}
                    <Form.Group className="mb-4" style={{ display: 'none' }}>
                      <Form.Control
                        type="text"
                        name="honeypot"
                        value={formData.honeypot}
                        onChange={handleInputChange}
                        tabIndex="-1"
                        autoComplete="off"
                      />
                    </Form.Group>

                    <Button 
                      type="submit" 
                      variant="primary" 
                      size="lg" 
                      className="cta-button w-100"
                      disabled={submitted || isSubmitting || cooldownTime > 0}
                    >
                      <FaPaperPlane className="me-2" />
                      {submitted ? 'Message Sent!' : 
                       cooldownTime > 0 ? `Please wait ${cooldownTime}s` : 
                       'Send Message'}
                    </Button>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Additional Information */}
      <section className="section-padding bg-light">
        <Container>
          <Row>
            <Col className="text-center">
              <h2 className="mb-4">Why Contact NIKDIP?</h2>
              <Row>
                <Col md={4} className="mb-4">
                  <div className="d-flex flex-column align-items-center">
                    <div className="feature-icon mb-3">
                      <FaEnvelope />
                    </div>
                    <h5>Quick Response</h5>
                    <p className="small">We respond to all inquiries within 24 hours</p>
                  </div>
                </Col>
                <Col md={4} className="mb-4">
                  <div className="d-flex flex-column align-items-center">
                    <div className="feature-icon mb-3">
                      <FaPhone />
                    </div>
                    <h5>Expert Support</h5>
                    <p className="small">Speak directly with water treatment specialists</p>
                  </div>
                </Col>
                <Col md={4} className="mb-4">
                  <div className="d-flex flex-column align-items-center">
                    <div className="feature-icon mb-3">
                      <FaMapMarkerAlt />
                    </div>
                    <h5>Local Presence</h5>
                    <p className="small">Strong presence in Nigeria with global reach</p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default Contact;
